import { useMemo, useState } from 'react';
import { ApiError, errorMessage } from '../../lib/api';
import type { AnswerIssue, AnswerValue, Answers, FormDefinition } from '../../lib/types';
import { cx, fileUrl } from '../../lib/utils';
import { countAnswered, validateSection } from '../../lib/validation';
import { ProgressBar } from '../../components/ui';
import { QuestionBlock } from './QuestionBlock';
import { useAutosave, type SaveState } from './useAutosave';

interface Props {
  form: FormDefinition;
  clientName?: string;
  initialAnswers?: Answers;
  /** Link slug; undefined in preview mode. */
  slug?: string;
  onSaveDraft?: (answers: Answers) => Promise<unknown>;
  onSubmit?: (answers: Answers) => Promise<void>;
  preview?: boolean;
}

const SAVE_LABEL: Record<SaveState, string> = {
  idle: '',
  saving: 'Saving…',
  saved: 'All changes saved',
  error: 'Could not save — check your connection',
};

/** Step-by-step form: one section per page, autosaved, validated before moving on. */
export function FormRenderer({
  form,
  clientName,
  initialAnswers = {},
  slug,
  onSaveDraft,
  onSubmit,
  preview,
}: Props) {
  const sections = useMemo(
    () => form.sections.filter((s) => s.questions.length > 0),
    [form.sections],
  );
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>(initialAnswers);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  const autosave = useAutosave(answers, onSaveDraft);

  const totalQuestions = sections.reduce((n, s) => n + s.questions.length, 0);
  const answered = countAnswered(sections, answers);
  const section = sections[step];
  const isLast = step === sections.length - 1;

  if (!section) return <p className="muted">This form has no questions yet.</p>;

  function setAnswer(id: string, value: AnswerValue) {
    setAnswers((a) => ({ ...a, [id]: value }));
    if (errors[id]) setErrors(({ [id]: _removed, ...rest }) => rest);
  }

  function scrollTo(questionId?: string) {
    requestAnimationFrame(() => {
      const el = questionId ? document.getElementById(`question-${questionId}`) : null;
      (el ?? document.getElementById('form-top'))?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }

  function goTo(next: number) {
    void autosave.flush();
    setStep(next);
    setSubmitError(undefined);
    scrollTo();
  }

  function handleNext() {
    const sectionErrors = validateSection(section!, answers);
    if (Object.keys(sectionErrors).length) {
      setErrors(sectionErrors);
      scrollTo(Object.keys(sectionErrors)[0]);
      return;
    }
    goTo(step + 1);
  }

  async function handleSubmit() {
    // Validate every section and jump to the first one with a problem.
    for (let i = 0; i < sections.length; i++) {
      const sectionErrors = validateSection(sections[i]!, answers);
      if (Object.keys(sectionErrors).length) {
        setErrors(sectionErrors);
        setStep(i);
        scrollTo(Object.keys(sectionErrors)[0]);
        return;
      }
    }
    if (!onSubmit) return;

    setSubmitting(true);
    setSubmitError(undefined);
    try {
      await autosave.flush();
      await onSubmit(answers);
      autosave.markSaved();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422 && Array.isArray(err.details)) {
        const issues = err.details as AnswerIssue[];
        const map = Object.fromEntries(issues.map((i) => [i.questionId, i.message]));
        const idx = sections.findIndex((s) => s.questions.some((q) => map[q.id]));
        setErrors(map);
        if (idx >= 0) setStep(idx);
        scrollTo(issues[0]?.questionId);
      }
      setSubmitError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  // Question numbers continue across sections.
  const firstNumber = sections.slice(0, step).reduce((n, s) => n + s.questions.length, 1);

  return (
    <div className="form-shell" id="form-top">
      <header className="form-header">
        {form.logoImageId && <img className="form-logo" src={fileUrl(form.logoImageId)} alt="" />}
        <h1>{form.name}</h1>
        {clientName && <p className="form-client">Prepared for {clientName}</p>}
        {step === 0 && form.description && <p className="form-description">{form.description}</p>}
      </header>

      <div className="form-progress">
        <div className="row-between">
          <span>
            Section {step + 1} of {sections.length}
          </span>
          <span className="muted">
            {answered} / {totalQuestions} answered
          </span>
        </div>
        <ProgressBar value={answered} max={totalQuestions} />
        <ol className="stepper" aria-label="Sections">
          {sections.map((s, i) => (
            <li key={s.id}>
              <button
                type="button"
                className={cx('step-dot', i === step && 'active', i < step && 'done')}
                title={s.title}
                aria-current={i === step ? 'step' : undefined}
                onClick={() => goTo(i)}
              >
                <span className="sr-only">{s.title}</span>
              </button>
            </li>
          ))}
        </ol>
      </div>

      <section className="card form-section">
        <h2>{section.title}</h2>
        {section.description && <p className="section-description">{section.description}</p>}
        {section.imageId && <img className="section-image" src={fileUrl(section.imageId)} alt="" />}

        {section.questions.map((q, i) => (
          <QuestionBlock
            key={q.id}
            question={q}
            number={firstNumber + i}
            value={answers[q.id]}
            onChange={(v) => setAnswer(q.id, v)}
            error={errors[q.id]}
            slug={slug}
          />
        ))}
      </section>

      {submitError && <div className="alert alert-error">{submitError}</div>}

      <div className="form-nav">
        <button
          type="button"
          className="btn btn-secondary"
          disabled={step === 0}
          onClick={() => goTo(step - 1)}
        >
          Back
        </button>
        <span className={cx('save-state', `save-${autosave.state}`)} aria-live="polite">
          {preview ? 'Preview — answers are not saved' : SAVE_LABEL[autosave.state]}
        </span>
        {isLast ? (
          <button
            type="button"
            className="btn btn-primary"
            disabled={submitting || preview}
            onClick={() => void handleSubmit()}
          >
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        ) : (
          <button type="button" className="btn btn-primary" onClick={handleNext}>
            Next
          </button>
        )}
      </div>
    </div>
  );
}
